import React, { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTRPC, trpcClient } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { toast } from "sonner";
import posthog from "posthog-js";
import { FileSignature, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AiSuggestionBanner } from "@/components/ai-suggestion/ai-suggestion-banner";
import {
  AiSuggestedMarker,
  AI_SUGGESTED_CONTROL_CLASSES,
} from "@/components/ai-suggestion/ai-suggested-marker";
import {
  useAiSuggestions,
  SuggestionOutcome,
  type SuggestionOutcomeEntry,
} from "@/components/ai-suggestion/use-ai-suggestions";
import { suggestionSourcesForAnchor } from "@/components/ai-suggestion/suggestion-source-priority";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  FormFieldType,
  FormSuggestionState,
  FormValueSuggestionSource,
} from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import {
  LocalFileInput,
  type LocalFile,
} from "@/components/file-upload/local-file-input";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import {
  declareUploadFiles,
  type DeclaredUploadFile,
} from "@/ajax/upload/declare-upload-file";
import { SignatureCanvas } from "./pdf/signature-canvas";
import { PdfSignEmbed } from "./pdf/pdf-sign-embed";
import type { FormAnchorRef } from "./form-anchor-ref";
import { useFormAutofill, type FieldSuggestion } from "./use-form-autofill";

const FORM_SUBMISSION_DIALOG_CLASS_NAME =
  "[&.dialog-content]:w-[calc(100vw-2rem)] [&.dialog-content]:max-h-[calc(100dvh-2rem)] [&.dialog-content]:overflow-hidden flex min-h-0 min-w-0 flex-col gap-0 rounded-2xl border-slate-200 bg-white p-0 text-slate-900 shadow-xl sm:rounded-2xl";
const FORM_SUBMISSION_HEADER_CLASS_NAME =
  "max-h-[35dvh] min-w-0 shrink-0 overflow-y-auto border-b border-slate-200 bg-white p-4 pr-12 sm:p-6 sm:pr-12";

type FormForSubmission =
  RouterOutputs["organizationForm"]["getFormForSubmission"];
type FormField = FormForSubmission["fields"][number];

interface FieldValueState {
  textValue: string;
  selectedOptions: string[];
}

// MultiSelect stores comma-joined option values, so a suggestion value must
// be split back out; every other field type is a plain string.
function suggestionToFieldValue(
  field: FormField,
  suggestion: FieldSuggestion,
): FieldValueState {
  const fieldType = field.fieldType as FormFieldType;
  if (fieldType === FormFieldType.MultiSelect) {
    return { textValue: "", selectedOptions: suggestion.value.split(",") };
  }
  return { textValue: suggestion.value, selectedOptions: [] };
}

/**
 * MultiSelect keeps its value in `selectedOptions` with `textValue` empty, so
 * comparing `textValue` alone would report every multi-select as changed.
 */
function fieldValueEquals(
  fieldType: FormFieldType,
  value: FieldValueState,
  suggestedValue: string,
): boolean {
  if (fieldType === FormFieldType.MultiSelect) {
    return value.selectedOptions.join(",") === suggestedValue;
  }
  return value.textValue === suggestedValue;
}

/**
 * Provenance for the server to persist alongside the value. File fields carry
 * none — their value is the upload, not text.
 */
function suggestionProvenance({
  entry,
  textValue,
  isFileBasedField,
}: {
  entry: SuggestionOutcomeEntry<FieldSuggestion> | undefined;
  textValue: string | null;
  isFileBasedField: boolean;
}): {
  suggestionState?: FormSuggestionState;
  suggestedValue?: string;
  suggestionSource?: FormValueSuggestionSource;
} {
  if (isFileBasedField || !textValue) {
    return {};
  }

  if (!entry) {
    return { suggestionState: FormSuggestionState.Typed };
  }

  const suggested = {
    suggestedValue: entry.suggestion.value,
    suggestionSource: entry.suggestion.source,
  };
  if (entry.outcome === SuggestionOutcome.Dismissed) {
    return {
      suggestionState: FormSuggestionState.DismissedThenTyped,
      ...suggested,
    };
  }
  // A suggested field whose value drifted was edited through a path that
  // didn't announce itself — the value is the provider's, not accepted.
  const isUntouched =
    entry.outcome === SuggestionOutcome.Suggested &&
    textValue === entry.suggestion.value;
  return {
    suggestionState: isUntouched
      ? FormSuggestionState.Accepted
      : FormSuggestionState.Edited,
    ...suggested,
  };
}

function FormFieldInput({
  field,
  value,
  onChange,
  localFiles,
  onLocalFilesChange,
  isSuggested,
  suggestedDescriptionId,
}: {
  field: FormField;
  value: FieldValueState;
  onChange: (next: FieldValueState) => void;
  localFiles: LocalFile[];
  onLocalFilesChange: (files: LocalFile[]) => void;
  isSuggested: boolean;
  suggestedDescriptionId: string;
}) {
  const fieldType = field.fieldType as FormFieldType;
  const describedBy = isSuggested ? suggestedDescriptionId : undefined;

  switch (fieldType) {
    case FormFieldType.Text:
      return (
        <Input
          id={field.id}
          placeholder={field.description ?? ""}
          value={value.textValue}
          onChange={(e) => onChange({ ...value, textValue: e.target.value })}
          className={cn(isSuggested && AI_SUGGESTED_CONTROL_CLASSES)}
          aria-describedby={describedBy}
        />
      );

    case FormFieldType.Textarea:
      return (
        <Textarea
          id={field.id}
          placeholder={field.description ?? ""}
          rows={3}
          value={value.textValue}
          onChange={(e) => onChange({ ...value, textValue: e.target.value })}
          className={cn(isSuggested && AI_SUGGESTED_CONTROL_CLASSES)}
          aria-describedby={describedBy}
        />
      );

    case FormFieldType.Date:
      return (
        <Input
          id={field.id}
          type="date"
          value={value.textValue}
          onChange={(e) => onChange({ ...value, textValue: e.target.value })}
          className={cn(isSuggested && AI_SUGGESTED_CONTROL_CLASSES)}
          aria-describedby={describedBy}
        />
      );

    case FormFieldType.Select:
      return (
        <Select
          value={value.textValue}
          onValueChange={(val) => onChange({ ...value, textValue: val })}
        >
          <SelectTrigger
            id={field.id}
            className={cn(isSuggested && AI_SUGGESTED_CONTROL_CLASSES)}
            aria-describedby={describedBy}
          >
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case FormFieldType.MultiSelect:
      return (
        <div
          id={field.id}
          tabIndex={-1}
          role="group"
          aria-describedby={describedBy}
          className={cn(
            "min-w-0 overflow-hidden rounded-xl border border-slate-400 bg-white divide-y divide-slate-200",
            isSuggested && "border-blue-300 bg-blue-50",
          )}
        >
          {(field.options ?? []).map((opt) => {
            const isChecked = value.selectedOptions.includes(opt);
            return (
              <label
                key={opt}
                htmlFor={`${field.id}-${opt}`}
                className={`flex min-h-12 items-center gap-3 px-3 py-3 cursor-pointer transition-colors ${
                  isChecked ? "bg-primary/5" : "hover:bg-slate-50"
                }`}
              >
                <Checkbox
                  id={`${field.id}-${opt}`}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...value.selectedOptions, opt]
                      : value.selectedOptions.filter((o) => o !== opt);
                    onChange({ ...value, selectedOptions: next });
                  }}
                />
                <span
                  className={`min-w-0 text-sm leading-6 [overflow-wrap:anywhere] ${
                    isChecked ? "text-primary font-medium" : "text-slate-700"
                  }`}
                >
                  {opt}
                </span>
              </label>
            );
          })}
        </div>
      );

    case FormFieldType.Checkbox:
      return (
        <label
          htmlFor={field.id}
          className={cn(
            "flex min-h-12 min-w-0 items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer transition-colors",
            value.textValue === "true"
              ? "border-primary/30 bg-primary/5"
              : "border-slate-400 bg-white hover:bg-slate-50",
            isSuggested && AI_SUGGESTED_CONTROL_CLASSES,
          )}
        >
          <Checkbox
            id={field.id}
            checked={value.textValue === "true"}
            onCheckedChange={(checked) =>
              onChange({ ...value, textValue: checked ? "true" : "false" })
            }
            aria-describedby={describedBy}
            className="mt-0.5 shrink-0"
          />
          <span
            className={`min-w-0 text-sm leading-6 [overflow-wrap:anywhere] ${
              value.textValue === "true"
                ? "text-primary font-medium"
                : "text-slate-700"
            }`}
          >
            {field.label}
          </span>
        </label>
      );

    case FormFieldType.FileUpload:
      return (
        <LocalFileInput files={localFiles} onFilesChange={onLocalFilesChange} />
      );

    case FormFieldType.Signature:
      return (
        <SignatureCanvas
          hasCaptured={localFiles.length > 0}
          onCapture={(capturedFile) => onLocalFilesChange([capturedFile])}
          onClear={() => onLocalFilesChange([])}
        />
      );

    default:
      return (
        <Input
          id={field.id}
          value={value.textValue}
          onChange={(e) => onChange({ ...value, textValue: e.target.value })}
          className={cn(isSuggested && AI_SUGGESTED_CONTROL_CLASSES)}
          aria-describedby={describedBy}
        />
      );
  }
}

// ─── Main dialog ────────────────────────────────────────────────────────────

interface FormSubmissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  /** The assignment or hiring-process item this submission fulfills. */
  anchor: FormAnchorRef;
  /** Called after a successful submit/sign, in addition to any hiring-process invalidation below. */
  onCompleted?: () => void;
  /** Prefill empty fields from the user's own data using anchor-specific sources; each stays marked as suggested until edited or dismissed. */
  suggestionsEnabled?: boolean;
  /** Off when the caller already shows the form as submitted, so the toast doesn't read as the whole flow being done. */
  showSuccessToast?: boolean;
}

export function FormSubmissionDialog({
  open,
  onOpenChange,
  formId,
  anchor,
  onCompleted,
  suggestionsEnabled = false,
  showSuccessToast = true,
}: FormSubmissionDialogProps) {
  const trpc = useTRPC();

  const jobPostId = anchor.kind === "hiring" ? anchor.jobPostId : undefined;

  const { data: formData, isLoading } = useQuery(
    trpc.organizationForm.getFormForSubmission.queryOptions(
      { formId },
      { enabled: open },
    ),
  );

  const isPdf = formData?.templateKind === "file";

  const {
    suggestions,
    vetoedFieldIds,
    isThinking: isAutofillThinking,
    hasSettled: hasAutofillSettled,
    deterministicCount,
    aiAnswerCount,
    reset: resetAutofill,
  } = useFormAutofill({
    formId,
    sources: suggestionSourcesForAnchor(anchor),
    enabled: open && Boolean(formData) && !isPdf && suggestionsEnabled,
  });

  const [fieldValues, setFieldValues] = useState<
    Record<string, FieldValueState>
  >({});
  const [localFiles, setLocalFiles] = useState<Record<string, LocalFile[]>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fieldValuesRef = useRef(fieldValues);
  fieldValuesRef.current = fieldValues;
  const [isPdfSignBusy, setIsPdfSignBusy] = useState(false);

  const submitMutation = useMutation(
    trpc.organizationForm.submitForm.mutationOptions({
      onError: () => {
        toast.error("Failed to submit form");
      },
    }),
  );

  const getFieldValue = (fieldId: string): FieldValueState => {
    return fieldValues[fieldId] ?? { textValue: "", selectedOptions: [] };
  };

  // Suggestions apply into currently-empty fields as each pass arrives; see
  // useAiSuggestions for the seed-once-per-key mechanics.
  const {
    appliedSuggestions,
    appliedCount,
    suggestionOutcomes,
    seededCount,
    totalSuggestionCount,
    dismissSuggestion,
    dismissAllSuggestions,
    markSuggestionEdited,
    retractSuggestion,
    reset: resetAiSuggestions,
  } = useAiSuggestions<FieldSuggestion>({
    suggestions,
    getSuggestionKey: (suggestion) => suggestion.formFieldId,
    isSuggestionEligible: (suggestion) => {
      if (!formData) {
        return false;
      }
      const field = formData.fields.find(
        (f) => f.id === suggestion.formFieldId,
      );
      if (!field) {
        return false;
      }

      const existing = fieldValuesRef.current[suggestion.formFieldId];
      return (
        !existing ||
        (existing.textValue.trim() === "" &&
          existing.selectedOptions.length === 0)
      );
    },
    onApply: (suggestion) => {
      if (!formData) {
        return;
      }
      const field = formData.fields.find(
        (f) => f.id === suggestion.formFieldId,
      );
      if (!field) {
        return;
      }
      setFieldValues((prev) => ({
        ...prev,
        [suggestion.formFieldId]: suggestionToFieldValue(field, suggestion),
      }));
    },
    onDismiss: (suggestion) => {
      setFieldValues((prev) => ({
        ...prev,
        [suggestion.formFieldId]: { textValue: "", selectedOptions: [] },
      }));
      setFieldErrors((prev) => {
        if (!(suggestion.formFieldId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[suggestion.formFieldId];
        return next;
      });
    },
  });

  // A veto lands seconds after the fast pass painted, so retract only while
  // the field still holds exactly what was suggested — anything the user
  // touched is theirs. State is read through a ref so the effect fires once
  // per veto batch rather than on every keystroke.
  const vetoedCountRef = useRef(0);
  const applyVetoesRef = useRef<(fieldIds: string[]) => void>(() => {});
  applyVetoesRef.current = (fieldIds) => {
    for (const fieldId of fieldIds) {
      const entry = suggestionOutcomes[fieldId];
      if (!entry || entry.outcome !== SuggestionOutcome.Suggested) {
        continue;
      }
      const field = formData?.fields.find((f) => f.id === fieldId);
      if (!field) {
        continue;
      }
      const current = fieldValuesRef.current[fieldId] ?? {
        textValue: "",
        selectedOptions: [],
      };
      if (
        !fieldValueEquals(
          field.fieldType as FormFieldType,
          current,
          entry.suggestion.value,
        )
      ) {
        continue;
      }
      retractSuggestion(fieldId);
      vetoedCountRef.current += 1;
    }
  };
  useEffect(() => {
    if (vetoedFieldIds.length === 0) {
      return;
    }
    applyVetoesRef.current(vetoedFieldIds);
  }, [vetoedFieldIds]);

  // Each open is its own session: Cancel/Escape never reach the post-submit
  // reset, so clear on close. Held in a ref so the effect depends on `open`
  // alone — the reset callbacks are new every render.
  const clearSessionRef = useRef<() => void>(() => {});
  clearSessionRef.current = () => {
    setFieldValues({});
    setLocalFiles({});
    setFieldErrors({});
    resetAiSuggestions();
    resetAutofill();
    vetoedCountRef.current = 0;
  };

  useEffect(() => {
    if (open) {
      return;
    }
    clearSessionRef.current();
  }, [open]);

  // Report once both passes settle — a second event per open would double
  // every chart built on this one.
  const hasReportedSuggestedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasReportedSuggestedRef.current = false;
      return;
    }
    if (!suggestionsEnabled || isPdf || !formData) {
      return;
    }
    if (!hasAutofillSettled || hasReportedSuggestedRef.current) {
      return;
    }
    hasReportedSuggestedRef.current = true;

    const eligibleFieldCount = formData.fields.filter(
      (field) =>
        field.fieldType !== FormFieldType.FileUpload &&
        field.fieldType !== FormFieldType.Signature,
    ).length;
    posthog.capture("form_autofill.suggested", {
      formId,
      eligibleFieldCount,
      // Counted from the settled responses, not from the hook's seeding
      // state: seeding commits a render later, so reading it here would
      // report the deterministic pass alone and then never correct itself.
      suggestedFieldCount: deterministicCount + aiAnswerCount,
      deterministicCount,
      aiAnswerCount,
    });
  }, [
    open,
    suggestionsEnabled,
    isPdf,
    formData,
    hasAutofillSettled,
    deterministicCount,
    aiAnswerCount,
    formId,
  ]);

  const validateFields = (): boolean => {
    if (!formData) {
      return false;
    }
    const errors: Record<string, string> = {};

    for (const field of formData.fields) {
      if (!field.isRequired) {
        continue;
      }
      const val = getFieldValue(field.id);
      const fieldType = field.fieldType as FormFieldType;

      switch (fieldType) {
        case FormFieldType.Checkbox:
          if (val.textValue !== "true") {
            errors[field.id] = "This field must be checked";
          }
          break;
        case FormFieldType.MultiSelect:
          if (val.selectedOptions.length === 0) {
            errors[field.id] = "Please select at least one option";
          }
          break;
        case FormFieldType.FileUpload:
          if ((localFiles[field.id] ?? []).length === 0) {
            errors[field.id] = "Please upload a file";
          }
          break;
        case FormFieldType.Signature:
          if ((localFiles[field.id] ?? []).length === 0) {
            errors[field.id] = "Please provide a signature";
          }
          break;
        default:
          if (!val.textValue.trim()) {
            errors[field.id] = "This field is required";
          }
          break;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!formData) {
      return;
    }
    if (!validateFields()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Each file's reservation carries its hash, computed before the payload
      // is built (the mapping below is synchronous).
      const declaredFilesByFieldId = new Map<string, DeclaredUploadFile[]>();
      for (const [fieldId, fieldLocalFiles] of Object.entries(localFiles)) {
        declaredFilesByFieldId.set(
          fieldId,
          await declareUploadFiles(fieldLocalFiles.map((lf) => lf.file)),
        );
      }

      const values = formData.fields.map((field) => {
        const val = getFieldValue(field.id);
        const fieldType = field.fieldType as FormFieldType;

        const isFileBasedField =
          fieldType === FormFieldType.FileUpload ||
          fieldType === FormFieldType.Signature;

        let textValue: string | null = null;
        if (fieldType === FormFieldType.MultiSelect) {
          textValue =
            val.selectedOptions.length > 0
              ? val.selectedOptions.join(",")
              : null;
        } else if (!isFileBasedField && val.textValue.trim()) {
          textValue = val.textValue.trim();
        }

        const fieldFiles = declaredFilesByFieldId.get(field.id) ?? [];
        const files =
          isFileBasedField && fieldFiles.length > 0 ? fieldFiles : undefined;

        return {
          formFieldId: field.id,
          textValue,
          files,
          ...suggestionProvenance({
            entry: suggestionOutcomes[field.id],
            textValue,
            isFileBasedField,
          }),
        };
      });

      if (totalSuggestionCount > 0) {
        posthog.capture("form_autofill.submitted", {
          formId,
          suggestedFieldCount: totalSuggestionCount,
          // Fields still in suggested state at submit — applied at seed time
          // and never edited or dismissed by the user.
          acceptedUneditedCount: appliedCount,
          dismissedOrEditedCount: seededCount - appliedCount,
          vetoedCount: vetoedCountRef.current,
        });
      }

      // Step 1: Submit the form (creates submission + file upload records)
      const result = await submitMutation.mutateAsync({
        formId,
        ...(anchor.kind === "assignment"
          ? { assignmentId: anchor.assignmentId }
          : {
              hiringProcessJobPostId: anchor.jobPostId,
              hiringProcessItemId: anchor.hiringProcessItemId,
            }),
        values,
      });

      // Step 2: Upload files to storage via TUS using the returned paths
      // and their upload tokens
      if (result.pendingUploads.length > 0) {
        await Promise.all(
          result.pendingUploads.map(async (upload) => {
            const fieldFiles =
              localFiles[formData.fields[upload.valueIndex]?.id] ?? [];
            const localFile = fieldFiles[upload.fileIndex];
            if (!localFile) {
              return;
            }

            await tusUploadFile({
              file: localFile.file,
              bucketName: upload.bucketName,
              objectName: upload.storagePath,
              uploadToken: upload.uploadToken,
            });
          }),
        );

        // Step 3: Confirm uploads and trigger virus scan
        await trpcClient.fileAttachment.confirmUploads.mutate({
          fileUploadIds: result.pendingUploads.map((u) => u.fileUploadId),
        });
      }

      if (jobPostId) {
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
        );
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcesses.queryOptions(),
        );
      }
      if (showSuccessToast) {
        toast.success("Form submitted successfully");
      }
      onCompleted?.();
      onOpenChange(false);
      setFieldValues({});
      setLocalFiles({});
      setFieldErrors({});
      resetAiSuggestions();
      resetAutofill();
      vetoedCountRef.current = 0;
    } catch {
      toast.error("Failed to submit form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // PDF form: wide dialog with DocuSeal embed
  if (isPdf) {
    return (
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          // While busy the embed may be mid prefill-apply: unmounting then
          // would write values into the vendor document with the disclosure
          // note never shown, and nothing persists to show it later. The
          // footer Cancel is already disabled on busy; this extends the same
          // gate to Escape, the overlay, and the built-in X.
          if (!nextOpen && isPdfSignBusy) {
            return;
          }
          onOpenChange(nextOpen);
        }}
      >
        <DialogContent
          className={cn(
            FORM_SUBMISSION_DIALOG_CLASS_NAME,
            "h-[90dvh] [&.dialog-content]:max-w-6xl",
          )}
        >
          {/* Header */}
          <div className={FORM_SUBMISSION_HEADER_CLASS_NAME}>
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <FileSignature size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-900 leading-6">
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {formData?.name ?? "Sign PDF Form"}
                  </span>
                  <span className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                    PDF
                  </span>
                </DialogTitle>
                {formData?.description && (
                  <p className="mt-1 text-sm text-slate-600 leading-6 [overflow-wrap:anywhere]">
                    {formData.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 min-w-0 overflow-auto bg-slate-50">
            <PdfSignEmbed
              formId={formId}
              anchor={anchor}
              onBusyChange={setIsPdfSignBusy}
              onSigned={(result) => {
                // Hiring-process items can use candidate-first signing order,
                // where the provider's own part being done is a success even
                // though the org still has to sign afterward — only a
                // genuinely unfinished document surfaces the "not complete"
                // error. (Chat form assignments always sign org-first, so
                // the org's part is already done by the time a provider
                // reaches this dialog.)
                const isFilled = result.fillStatus === "filled";
                if (
                  !isFilled &&
                  result.signerStatus.candidate !== "completed"
                ) {
                  toast.error(
                    "This document isn't complete yet. Fill in all required fields and click Complete in the form, then try again.",
                  );
                  return;
                }
                if (jobPostId) {
                  void queryClient.invalidateQueries(
                    trpc.hiringProcess.getMyProcess.queryOptions({
                      jobPostId,
                    }),
                  );
                  void queryClient.invalidateQueries(
                    trpc.hiringProcess.getMyProcesses.queryOptions(),
                  );
                }
                toast.success(
                  isFilled
                    ? "Form completed successfully"
                    : "Signed — waiting for the organization to complete their part",
                );
                onCompleted?.();
                onOpenChange(false);
              }}
              onError={(message) => toast.error(message)}
            />
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 p-4 sm:px-6 flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 basis-40 text-xs leading-5 text-slate-600 [overflow-wrap:anywhere]">
              Fill in all fields and complete the form to submit.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPdfSignBusy}
              className="min-h-12 shrink-0"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Native form
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          FORM_SUBMISSION_DIALOG_CLASS_NAME,
          "[&.dialog-content]:max-w-lg",
        )}
      >
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {/* Header */}
          <div
            className={cn(
              FORM_SUBMISSION_HEADER_CLASS_NAME,
              "max-h-none overflow-visible",
            )}
          >
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <FileText size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-900 leading-6">
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {formData?.name ?? "Fill Out Form"}
                  </span>
                  <span className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                    Form
                  </span>
                </DialogTitle>
                {formData?.description && (
                  <p className="mt-1 text-sm text-slate-600 leading-6 [overflow-wrap:anywhere]">
                    {formData.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="min-w-0 p-4 sm:px-6 sm:py-5">
            {isLoading ? (
              <div className="space-y-5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-3.5 bg-slate-200 rounded w-28 animate-pulse" />
                    <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
                  </div>
                ))}
              </div>
            ) : !formData ? (
              <p className="text-sm text-slate-600 py-4">Form not found.</p>
            ) : (
              <div className="space-y-5">
                {(appliedCount > 0 || isAutofillThinking) && (
                  <AiSuggestionBanner
                    count={appliedCount}
                    thinking={isAutofillThinking}
                    onDismissAll={dismissAllSuggestions}
                  />
                )}
                {formData.fields.map((field) => {
                  const fieldType = field.fieldType as FormFieldType;
                  const isCheckbox = fieldType === FormFieldType.Checkbox;
                  const hasError = Boolean(fieldErrors[field.id]);
                  const isSuggested = Boolean(appliedSuggestions[field.id]);
                  const suggestedDescriptionId = `${field.id}-suggested-desc`;

                  return (
                    <div
                      key={field.id}
                      className="min-w-0 space-y-2"
                      aria-live="polite"
                    >
                      {!isCheckbox && (
                        <div className="flex min-w-0 items-baseline gap-1">
                          <Label className="min-w-0 text-sm font-medium text-slate-700 [overflow-wrap:anywhere]">
                            {field.label}
                          </Label>
                          {field.isRequired && (
                            <span className="text-red-500 text-sm font-semibold leading-none">
                              *
                            </span>
                          )}
                        </div>
                      )}
                      {field.description && !isCheckbox && (
                        <p className="text-xs text-slate-600 leading-5 [overflow-wrap:anywhere]">
                          {field.description}
                        </p>
                      )}
                      {isSuggested && (
                        <div className="flex">
                          <AiSuggestedMarker
                            fieldLabel={field.label}
                            descriptionId={suggestedDescriptionId}
                            onDismiss={() => dismissSuggestion(field.id)}
                            controlId={field.id}
                          />
                        </div>
                      )}
                      <FormFieldInput
                        field={field}
                        value={getFieldValue(field.id)}
                        isSuggested={isSuggested}
                        suggestedDescriptionId={suggestedDescriptionId}
                        onChange={(next) => {
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.id]: next,
                          }));
                          // The user's own edit takes ownership of the value:
                          // keep it, drop the suggested marking.
                          if (appliedSuggestions[field.id]) {
                            markSuggestionEdited(field.id);
                          }
                          if (fieldErrors[field.id]) {
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n[field.id];
                              return n;
                            });
                          }
                        }}
                        localFiles={localFiles[field.id] ?? []}
                        onLocalFilesChange={(files) => {
                          setLocalFiles((prev) => ({
                            ...prev,
                            [field.id]: files,
                          }));
                          if (fieldErrors[field.id]) {
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n[field.id];
                              return n;
                            });
                          }
                        }}
                      />
                      {hasError && (
                        <p className="text-xs text-red-600 flex items-center gap-1 [overflow-wrap:anywhere]">
                          <span className="inline-block w-1 h-1 rounded-full bg-red-500 shrink-0" />
                          {fieldErrors[field.id]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 p-4 sm:px-6 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="min-h-12 flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
            className="min-h-12 flex-1 sm:flex-none"
          >
            {isSubmitting ? "Submitting..." : "Submit Form"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
